import { useCallback, useEffect, useRef, useState } from 'react';

// Speech-to-text, all reached directly from the browser (every endpoint below
// sends `access-control-allow-origin: *`, so no broker proxy is needed):
//   - scribe_v2_realtime — ElevenLabs streaming over a WebSocket. We mint a
//     single-use token with the user's key, open the WS with it (browser WS
//     can't send the xi-api-key header), and stream 16 kHz PCM captured from
//     the mic. Partial transcripts preview live; committed segments append to
//     the draft.
//   - scribe_v2 — ElevenLabs batch. Record with MediaRecorder, then POST the
//     whole blob to /v1/speech-to-text and append the returned text.
//   - groq_whisper_v3 / groq_whisper_v3_turbo — Groq's OpenAI-compatible batch
//     transcription (whisper-large-v3 / -turbo). Same MediaRecorder path as
//     scribe_v2, but POSTed to Groq with a `Bearer` key. Groq has no streaming
//     endpoint, so these are always batch.
export type ScribeModel = 'scribe_v2_realtime' | 'scribe_v2' | 'groq_whisper_v3' | 'groq_whisper_v3_turbo';

export const SCRIBE_MODEL_LABELS: Record<ScribeModel, string> = {
  scribe_v2: 'Scribe v2',
  scribe_v2_realtime: 'Scribe v2 Realtime',
  groq_whisper_v3: 'Whisper Large v3 (Groq)',
  groq_whisper_v3_turbo: 'Whisper Large v3 Turbo (Groq)',
};

// Models served by Groq rather than ElevenLabs. Maps our id → Groq's `model`
// form value. Used to pick the endpoint/key (Bearer vs xi-api-key) and to know
// a model is batch-only.
export const GROQ_SCRIBE_MODELS: Partial<Record<ScribeModel, string>> = {
  groq_whisper_v3: 'whisper-large-v3',
  groq_whisper_v3_turbo: 'whisper-large-v3-turbo',
};

export function isGroqScribeModel(model: ScribeModel): boolean {
  return model in GROQ_SCRIBE_MODELS;
}

// Spoken-language hint for transcription. `auto` (the default) lets ElevenLabs
// detect the language — we just omit `language_code`; any other value is an
// ISO-639-1 code passed straight through to the API.
export const SCRIBE_LANGUAGE_AUTO = 'auto';

export const SCRIBE_LANGUAGE_LABELS: Record<string, string> = {
  auto: 'Auto-detect',
  en: 'English',
  et: 'Estonian',
  fi: 'Finnish',
  ru: 'Russian',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  sv: 'Swedish',
  no: 'Norwegian',
  da: 'Danish',
  pl: 'Polish',
  uk: 'Ukrainian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  cs: 'Czech',
  tr: 'Turkish',
  ar: 'Arabic',
  hi: 'Hindi',
  zh: 'Chinese',
  ja: 'Japanese',
  ko: 'Korean',
};

type UseScribeOptions = {
  // ElevenLabs key (xi-api-key) — used by the scribe_* models.
  apiKey: string;
  // Groq key (Bearer) — used by the groq_whisper_* models.
  groqApiKey: string;
  model: ScribeModel;
  // Spoken language code, or 'auto' to let the provider detect it.
  language: string;
  // Append a finalized chunk of transcript to the composer draft.
  onText: (text: string) => void;
  // Fires once a dictation session has fully finished — the user stopped and
  // all transcription has settled (batch upload returned / realtime committed
  // its final segment). Used by the auto-submit option to send on its own.
  onDone?: () => void;
};

export type ScribeController = {
  recording: boolean;
  // Set the instant a start is requested, before the mic/token/WebSocket come
  // up. Lets the UI show the active (red) state immediately instead of after
  // the ~half-second network handshake. Clears once `recording` takes over.
  starting: boolean;
  // A batch upload is in flight (recording stopped, waiting on the transcript).
  busy: boolean;
  // Live, not-yet-committed transcript for the realtime model.
  partial: string;
  error: string | null;
  toggle: () => void;
  stop: () => void;
};

function floatTo16BitPCM(input: Float32Array): ArrayBuffer {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function useScribe({ apiKey, groqApiKey, model, language, onText, onDone }: UseScribeOptions): ScribeController {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Latest props read at start() time so a key/model change between renders is
  // always honoured without re-creating the controller.
  const apiKeyRef = useRef(apiKey);
  const groqApiKeyRef = useRef(groqApiKey);
  const modelRef = useRef(model);
  const languageRef = useRef(language);
  const onTextRef = useRef(onText);
  const onDoneRef = useRef(onDone);
  useEffect(() => { apiKeyRef.current = apiKey; }, [apiKey]);
  useEffect(() => { groqApiKeyRef.current = groqApiKey; }, [groqApiKey]);
  useEffect(() => { modelRef.current = model; }, [model]);
  useEffect(() => { languageRef.current = language; }, [language]);
  useEffect(() => { onTextRef.current = onText; }, [onText]);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  // Everything we may need to tear down lives here so stop()/unmount can clean
  // up regardless of which model started it.
  const sessionRef = useRef<{
    ws: WebSocket | null;
    ctx: AudioContext | null;
    processor: ScriptProcessorNode | null;
    source: MediaStreamAudioSourceNode | null;
    stream: MediaStream | null;
    recorder: MediaRecorder | null;
  }>({ ws: null, ctx: null, processor: null, source: null, stream: null, recorder: null });

  const teardownMedia = useCallback(() => {
    const s = sessionRef.current;
    try { s.processor?.disconnect(); } catch { /* already gone */ }
    try { s.source?.disconnect(); } catch { /* already gone */ }
    if (s.ctx && s.ctx.state !== 'closed') void s.ctx.close().catch(() => {});
    s.stream?.getTracks().forEach((t) => t.stop());
    s.processor = null;
    s.source = null;
    s.ctx = null;
    s.stream = null;
    s.recorder = null;
  }, []);

  const stop = useCallback(() => {
    const s = sessionRef.current;
    setRecording(false);
    setStarting(false);
    setPartial('');
    // Realtime: flush the last segment, give the server a beat to return it,
    // then close. Batch: stopping the recorder fires onstop → upload.
    if (s.recorder && s.recorder.state !== 'inactive') {
      try { s.recorder.stop(); } catch { /* ignore */ }
      return;
    }
    if (s.ws) {
      const ws = s.ws;
      // Drop our claim on this socket synchronously so a late onopen/onerror
      // (e.g. the user released Right Shift while it was still connecting)
      // bails instead of re-activating the UI.
      sessionRef.current.ws = null;
      try { s.processor?.disconnect(); } catch { /* ignore */ }
      try { s.source?.disconnect(); } catch { /* ignore */ }
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ message_type: 'input_audio_chunk', audio_base_64: '', commit: true, sample_rate: 16000 }));
        } catch { /* ignore */ }
        window.setTimeout(() => {
          try { ws.close(); } catch { /* ignore */ }
          teardownMedia();
          // The final committed segment has had its beat to arrive — the
          // session is fully done, so let auto-submit fire.
          onDoneRef.current?.();
        }, 1400);
      } else {
        // Still connecting — the held utterance was too short to have a live
        // session; drop the buffered pre-roll and abort immediately.
        try { ws.close(); } catch { /* ignore */ }
        teardownMedia();
      }
      return;
    }
    teardownMedia();
  }, [teardownMedia]);

  const startRealtime = useCallback(async () => {
    const key = apiKeyRef.current;

    // Kick the token request off in parallel with opening the mic, so we start
    // capturing audio *during* the (network-bound) handshake instead of only
    // after it. Chunks recorded before the socket is open are buffered and
    // flushed in order on open — so the first ~second of speech isn't lost.
    const tokenPromise = fetch('https://api.elevenlabs.io/v1/single-use-token/realtime_scribe', {
      method: 'POST',
      headers: { 'xi-api-key': key },
    });

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      // Don't leave the parallel token fetch as an unhandled rejection.
      tokenPromise.then((r) => r.body?.cancel()).catch(() => {});
      throw err;
    }
    const Ctx: typeof AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx({ sampleRate: 16000 });
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);

    // Not every browser honours a requested 16 kHz context (Safari forces the
    // hardware rate). Tell ElevenLabs the rate we actually got — both the
    // audio_format enum and the per-chunk sample_rate — so the audio is decoded
    // correctly instead of pitch-shifted.
    const SUPPORTED_RATES = [8000, 16000, 22050, 24000, 44100, 48000];
    const rate = SUPPORTED_RATES.includes(ctx.sampleRate) ? ctx.sampleRate : 16000;

    // Register the media on the session right away so stop()/teardown can clean
    // it up even if the user releases before the socket opens. `ws` stays null
    // until the handshake completes.
    Object.assign(sessionRef.current, { ws: null, ctx, processor, source, stream, recorder: null });

    // Pre-roll buffer for chunks captured before the socket is open.
    const pending: string[] = [];
    const MAX_PENDING = 240; // ~60s of 4096-sample frames — a safety cap, never hit normally
    let liveWs: WebSocket | null = null;

    // Start capturing immediately. ScriptProcessor needs a downstream connection
    // to be pumped; its output buffer is left silent so this never echoes the
    // mic to the speakers.
    source.connect(processor);
    processor.connect(ctx.destination);
    processor.onaudioprocess = (event) => {
      const b64 = arrayBufferToBase64(floatTo16BitPCM(event.inputBuffer.getChannelData(0)));
      if (liveWs && liveWs.readyState === WebSocket.OPEN) {
        liveWs.send(JSON.stringify({ message_type: 'input_audio_chunk', audio_base_64: b64, commit: false, sample_rate: rate }));
      } else if (pending.length < MAX_PENDING) {
        pending.push(b64);
      }
    };
    // We're genuinely capturing now (into the buffer), so reflect "Listening"
    // rather than waiting on the socket.
    setStarting(false);
    setRecording(true);

    let tokenRes: Response;
    try {
      tokenRes = await tokenPromise;
    } catch {
      throw new Error('Could not start (token request failed)');
    }
    if (!tokenRes.ok) {
      throw new Error(tokenRes.status === 401 ? 'Invalid ElevenLabs API key' : `Could not start (token ${tokenRes.status})`);
    }
    const { token } = await tokenRes.json();
    if (!token) throw new Error('No token returned by ElevenLabs');

    // The user may have released during the handshake — teardown nulls our media
    // claim, so bail without opening a socket.
    if (sessionRef.current.stream !== stream) return;

    const params = new URLSearchParams({
      model_id: 'scribe_v2_realtime',
      token,
      commit_strategy: 'vad',
      audio_format: `pcm_${rate}`,
    });
    const lang = languageRef.current;
    if (lang && lang !== SCRIBE_LANGUAGE_AUTO) params.set('language_code', lang);
    const ws = new WebSocket(`wss://api.elevenlabs.io/v1/speech-to-text/realtime?${params.toString()}`);
    sessionRef.current.ws = ws;

    ws.onopen = () => {
      // stop() nulls our claim on the socket if the user already released; in
      // that case don't start streaming, just close out.
      if (sessionRef.current.ws !== ws) { try { ws.close(); } catch { /* ignore */ } return; }
      // Flush everything captured during the handshake, in order, then go live.
      for (const b64 of pending) {
        try { ws.send(JSON.stringify({ message_type: 'input_audio_chunk', audio_base_64: b64, commit: false, sample_rate: rate })); } catch { /* ignore */ }
      }
      pending.length = 0;
      liveWs = ws;
    };
    ws.onmessage = (event) => {
      let data: { message_type?: string; text?: string; data?: { text?: string; transcript?: string } };
      try { data = JSON.parse(typeof event.data === 'string' ? event.data : ''); } catch { return; }
      const type = (data.message_type || '').toLowerCase();
      const text = data.text ?? data.data?.text ?? data.data?.transcript ?? '';
      if (type === 'partial_transcript') {
        setPartial(text);
      } else if (type === 'committed_transcript') {
        if (text.trim()) onTextRef.current(text.trim());
        setPartial('');
      } else if (type.includes('error')) {
        setError(text || 'Transcription error');
      }
    };
    ws.onerror = () => {
      if (sessionRef.current.ws !== ws) return; // aborted by stop() — not a real failure
      setError('Connection to ElevenLabs failed');
    };
  }, []);

  const startBatch = useCallback(async () => {
    const key = apiKeyRef.current;
    const groqKey = groqApiKeyRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    const chunks: Blob[] = [];
    Object.assign(sessionRef.current, { recorder, stream, ws: null, ctx: null, processor: null, source: null });
    recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      sessionRef.current.recorder = null;
      sessionRef.current.stream = null;
      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      if (!blob.size) return;
      setBusy(true);
      try {
        const lang = languageRef.current;
        const langCode = lang && lang !== SCRIBE_LANGUAGE_AUTO ? lang : '';
        const ext = (recorder.mimeType || 'audio/webm').includes('ogg') ? 'ogg' : 'webm';
        const groqModel = GROQ_SCRIBE_MODELS[modelRef.current];
        const form = new FormData();
        let res: Response;
        if (groqModel) {
          // Groq's OpenAI-compatible transcription endpoint: Bearer key, `model`
          // + (ISO-639-1) `language` form fields, JSON response with `text`.
          form.append('model', groqModel);
          form.append('response_format', 'json');
          if (langCode) form.append('language', langCode);
          form.append('file', blob, `speech.${ext}`);
          res = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
            method: 'POST',
            headers: { Authorization: `Bearer ${groqKey}` },
            body: form,
          });
          if (!res.ok) {
            throw new Error(res.status === 401 ? 'Invalid Groq API key' : `Transcription failed (${res.status})`);
          }
        } else {
          form.append('model_id', 'scribe_v2');
          if (langCode) form.append('language_code', langCode);
          form.append('file', blob, `speech.${ext}`);
          res = await fetch('https://api.elevenlabs.io/v1/speech-to-text', {
            method: 'POST',
            headers: { 'xi-api-key': key },
            body: form,
          });
          if (!res.ok) {
            throw new Error(res.status === 401 ? 'Invalid ElevenLabs API key' : `Transcription failed (${res.status})`);
          }
        }
        const data = await res.json();
        if (data?.text?.trim()) onTextRef.current(data.text.trim());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Transcription failed');
      } finally {
        setBusy(false);
      }
      // Transcript appended (or failed) — the session is done; auto-submit can
      // fire now. Skipped above when the recording was empty (early return).
      onDoneRef.current?.();
    };
    recorder.start();
    setStarting(false);
    setRecording(true);
  }, []);

  const toggle = useCallback(() => {
    if (recording || starting || busy) {
      stop();
      return;
    }
    setError(null);
    setPartial('');
    const groq = isGroqScribeModel(modelRef.current);
    if (!(groq ? groqApiKeyRef.current : apiKeyRef.current).trim()) {
      setError(groq ? 'Add your Groq API key in settings (the cog)' : 'Add your ElevenLabs API key in settings (the cog)');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone unavailable in this browser');
      return;
    }
    // Show the active state right away — the token fetch + WebSocket handshake
    // that precede the real `recording` flag take ~half a second, and waiting
    // on them made the mic feel unresponsive.
    setStarting(true);
    const run = modelRef.current === 'scribe_v2_realtime' ? startRealtime : startBatch;
    run().catch((err) => {
      setError(err instanceof Error ? err.message : 'Could not start microphone');
      setStarting(false);
      setRecording(false);
      teardownMedia();
    });
  }, [recording, starting, busy, stop, startRealtime, startBatch, teardownMedia]);

  // Tear everything down if the panel unmounts mid-recording.
  useEffect(() => () => { teardownMedia(); }, [teardownMedia]);

  return { recording, starting, busy, partial, error, toggle, stop };
}
